import Footer from "components/VerticalLayout/Footer";
import React, { useState, useEffect } from "react";
import { Card, Col, Container, Row, Input, Label, Button } from "reactstrap";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const EmiCalculate = () => {
    const [loanAmount, setLoanAmount] = useState(1000000);
    const [interestRate, setInterestRate] = useState(6.5);
    const [loanTenure, setLoanTenure] = useState(5);
    const [emi, setEmi] = useState(0);
    const [totalInterest, setTotalInterest] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [repaymentSchedule, setRepaymentSchedule] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const monthsPerPage = 12;

    useEffect(() => {
        const calculateEMI = () => {
            const principal = parseFloat(loanAmount);
            const rate = parseFloat(interestRate) / 12 / 100;
            const tenureMonths = parseFloat(loanTenure) * 12;

            const emiValue =
                (principal * rate * Math.pow(1 + rate, tenureMonths)) /
                (Math.pow(1 + rate, tenureMonths) - 1);

            const totalPayment = emiValue * tenureMonths;
            const interest = totalPayment - principal;

            setEmi(emiValue);
            setTotalInterest(interest);
            setTotalAmount(totalPayment);

            // Generate repayment schedule
            const schedule = [];
            let balance = principal;

            for (let month = 1; month <= tenureMonths; month++) {
                const interestForMonth = balance * rate;
                const principalForMonth = emiValue - interestForMonth;
                const endingBalance = balance - principalForMonth;

                schedule.push({
                    month: month, // Store month as number instead of string
                    beginningBalance: balance,
                    emi: emiValue,
                    principal: principalForMonth,
                    interest: interestForMonth,
                    endingBalance: endingBalance > 0 ? endingBalance : 0
                });

                balance = endingBalance;
            }

            setRepaymentSchedule(schedule);
            if (schedule.length > 0) {
                const firstYear = new Date().getFullYear();
                setSelectedYear(firstYear);
            }
        };

        calculateEMI();
    }, [loanAmount, interestRate, loanTenure]);

    const getYearsFromSchedule = () => {
        const years = [];
        if (repaymentSchedule.length === 0) return years;

        const startYear = new Date().getFullYear();
        const endYear = startYear + Math.ceil(repaymentSchedule.length / 12) - 1;

        for (let year = startYear; year <= endYear; year++) {
            years.push(year);
        }

        return years;
    };

    const getScheduleForYear = () => {
        if (!selectedYear || repaymentSchedule.length === 0) return [];

        const startYear = new Date().getFullYear();
        const yearIndex = selectedYear - startYear;
        const startMonth = yearIndex * 12;
        const endMonth = startMonth + 12;

        return repaymentSchedule.slice(startMonth, endMonth);
    };

    const getMonthName = (monthNumber) => {
        const monthNames = ["January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"];
        return monthNames[(monthNumber - 1) % 12];
    };

    const scheduleForYear = getScheduleForYear();
    const totalPages = Math.ceil(scheduleForYear.length / monthsPerPage);
    const paginatedSchedule = scheduleForYear.slice(
        currentPage * monthsPerPage,
        (currentPage + 1) * monthsPerPage
    );

    const pieData = [
        { name: "Principal", value: parseFloat(loanAmount) },
        { name: "Interest", value: totalInterest },
    ];

    const COLORS = ["#28a745", "#ff7f50"]; // green and coral orange

    useEffect(() => {
        // Dynamically load the widget script
        const script = document.createElement("script");
        script.src = "https://ocpomni.alendei.io/js/widget/mzzpadohxkpzkhtr/float.js";
        script.id = "wa-widget";
        script.setAttribute("widget-id", "aaa5le"); // works as custom attribute
        script.async = true;
        document.body.appendChild(script);

        return () => {
            // Cleanup when component unmounts
            document.body.removeChild(script);
        };
    }, []);

    return (
        <React.Fragment >
            <Container fluid className="py-5"><br></br><br></br><br></br><br></br>
                <Row className="justify-content-center">
                    <Col lg={10}>
                        <Card
                            style={{
                                background: "rgba(255, 255, 255, 0.1)",
                                borderRadius: "12px",
                                boxShadow: "0 8px 20px rgba(0, 0, 0, 0.08)",
                                padding: "2rem",
                            }}
                        >
                            <Row className="gy-4 align-items-center">
                                <Col md={7}>
                                    <h3 className="mb-4 fw-bold text-primary">EMI Calculator</h3>

                                    {/* Loan Amount */}
                                    <div className="mb-4">
                                        <Label className="form-label fw-semibold" style={{ color: "white" }}>Loan amount</Label>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <Input
                                                type="number"
                                                className="form-control"
                                                style={{
                                                    backgroundColor: "#e6fff2",
                                                    color: "#00b386",
                                                    fontWeight: "bold",
                                                    border: "none",
                                                    maxWidth: "120px",
                                                }}
                                                value={loanAmount}
                                                onChange={(e) => setLoanAmount(Number(e.target.value))}
                                                min="0"
                                                step="1000"
                                            />
                                            <span style={{ backgroundColor: "#e6fff2", color: "#00b386", fontWeight: "bold", padding: "4px 8px", borderRadius: "4px" }}>
                                                ₹ {loanAmount.toLocaleString()}
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="100000"
                                            max="5000000"
                                            step="1000"
                                            value={loanAmount}
                                            onChange={(e) => setLoanAmount(Number(e.target.value))}
                                            style={{ width: "100%", marginTop: "6px" }}
                                        />
                                    </div>

                                    {/* Rate of Interest */}
                                    <div className="mb-4">
                                        <Label className="form-label fw-semibold" style={{ color: "white" }}>Rate of interest (p.a)</Label>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                className="form-control"
                                                style={{
                                                    backgroundColor: "#e6fff2",
                                                    color: "#00b386",
                                                    fontWeight: "bold",
                                                    border: "none",
                                                    maxWidth: "120px",
                                                }}
                                                value={interestRate}
                                                onChange={(e) => setInterestRate(Number(e.target.value))}
                                                min="0"
                                                max="20"
                                            />
                                            <span style={{ backgroundColor: "#e6fff2", color: "#00b386", fontWeight: "bold", padding: "4px 8px", borderRadius: "4px" }}>
                                                {interestRate.toFixed(1)} %
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="20"
                                            step="0.1"
                                            value={interestRate}
                                            onChange={(e) => setInterestRate(Number(e.target.value))}
                                            style={{ width: "100%", marginTop: "6px" }}
                                        />
                                    </div>

                                    {/* Loan Tenure */}
                                    <div className="mb-4">
                                        <Label className="form-label fw-semibold" style={{ color: "white" }}>Loan tenure</Label>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <Input
                                                type="number"
                                                className="form-control"
                                                style={{
                                                    backgroundColor: "#e6fff2",
                                                    color: "#00b386",
                                                    fontWeight: "bold",
                                                    border: "none",
                                                    maxWidth: "120px",
                                                }}
                                                value={loanTenure}
                                                onChange={(e) => setLoanTenure(Number(e.target.value))}
                                                min="1"
                                                max="30"
                                            />
                                            <span style={{ backgroundColor: "#e6fff2", color: "#00b386", fontWeight: "bold", padding: "4px 8px", borderRadius: "4px" }}>
                                                {loanTenure} Yr
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="1"
                                            max="30"
                                            step="1"
                                            value={loanTenure}
                                            onChange={(e) => setLoanTenure(Number(e.target.value))}
                                            style={{ width: "100%", marginTop: "6px" }}
                                        />
                                    </div>

                                    <div className="border-top pt-4">
                                        <p className="mb-2" style={{ color: "white" }}>
                                            <strong>Monthly EMI:</strong> ₹{emi.toFixed(0).toLocaleString()}
                                        </p>
                                        <p className="mb-2" style={{ color: "white" }}>
                                            <strong>Principal amount:</strong> ₹{Number(loanAmount).toLocaleString()}
                                        </p>
                                        <p className="mb-2" style={{ color: "white" }}>
                                            <strong>Total interest:</strong>{" "}
                                            ₹{totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                        <p style={{ color: "white" }}>
                                            <strong>Total amount:</strong>{" "}
                                            ₹{totalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                    </div>
                                </Col>

                                <Col md={5} className="d-flex justify-content-center align-items-center">
                                    <ResponsiveContainer width="100%" height={280}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                dataKey="value"
                                                nameKey="name"
                                                outerRadius={90}
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `₹${Number(value).toLocaleString()}`} />
                                            <Legend verticalAlign="bottom" iconType="circle" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Col>
                            </Row>

                            {/* Repayment Table */}
                            <Row className="mt-5">
                                <Col>
                                    <h4 className="text-center mb-4 text-white">Your Repayment Details</h4>
                                    <div className="mb-3" style={{
                                        overflowX: "auto",
                                        whiteSpace: "nowrap",
                                        paddingBottom: "10px",
                                        scrollbarWidth: "thin"
                                    }}>
                                        <div className="d-flex justify-content-start gap-3" style={{ minWidth: "fit-content" }}>
                                            {getYearsFromSchedule().map(year => (
                                                <button
                                                    key={year}
                                                    onClick={() => { setSelectedYear(year); setCurrentPage(0); }}
                                                    style={{
                                                        border: 'none',
                                                        padding: '6px 16px',
                                                        borderBottom: selectedYear === year ? '3px solid #00b386' : 'none',
                                                        background: 'transparent',
                                                        color: selectedYear === year ? '#00b386' : 'white',
                                                        fontWeight: selectedYear === year ? 'bold' : 'normal',
                                                        cursor: 'pointer',
                                                        minWidth: '80px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {year}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{
                                        overflowX: "auto",
                                        backgroundColor: "#fff",
                                        borderRadius: "8px",
                                        padding: "16px",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                                    }}>
                                        <table className="table" style={{
                                            minWidth: "700px",
                                            borderCollapse: "separate",
                                            borderSpacing: "0"
                                        }}>
                                            <thead style={{
                                                background: "#00b386",
                                                color: "white",
                                                position: "sticky",
                                                top: "0"
                                            }}>
                                                <tr>
                                                    <th style={{ padding: "12px 16px", borderTopLeftRadius: "8px" }}>Month</th>
                                                    <th style={{ padding: "12px 16px" }}>Beginning Balance</th>
                                                    <th style={{ padding: "12px 16px" }}>EMI</th>
                                                    <th style={{ padding: "12px 16px" }}>Principal</th>
                                                    <th style={{ padding: "12px 16px" }}>Interest</th>
                                                    <th style={{ padding: "12px 16px", borderTopRightRadius: "8px" }}>Outstanding Balance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedSchedule.map((item, index) => (
                                                    <tr key={index} style={{
                                                        borderBottom: "1px solid #e0e0e0",
                                                        transition: "background 0.2s"
                                                    }}>
                                                        <td style={{ padding: "12px 16px", fontWeight: "500" }}>{getMonthName(item.month)}</td>
                                                        <td style={{ padding: "12px 16px" }}>₹ {item.beginningBalance.toLocaleString()}</td>
                                                        <td style={{ padding: "12px 16px" }}>₹ {item.emi.toLocaleString()}</td>
                                                        <td style={{ padding: "12px 16px", color: "#28a745" }}>₹ {item.principal.toLocaleString()}</td>
                                                        <td style={{ padding: "12px 16px", color: "#dc3545" }}>₹ {item.interest.toLocaleString()}</td>
                                                        <td style={{ padding: "12px 16px" }}>₹ {item.endingBalance.toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {scheduleForYear.length > monthsPerPage && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <Button
                                                    disabled={currentPage === 0}
                                                    onClick={() => setCurrentPage(p => p - 1)}
                                                    style={{
                                                        background: "#00b386",
                                                        border: "none",
                                                        padding: "8px 16px",
                                                        borderRadius: "4px"
                                                    }}
                                                >
                                                    Previous
                                                </Button>
                                                <span style={{ fontWeight: "500" }}>Page {currentPage + 1} of {totalPages}</span>
                                                <Button
                                                    disabled={currentPage + 1 >= totalPages}
                                                    onClick={() => setCurrentPage(p => p + 1)}
                                                    style={{
                                                        background: "#00b386",
                                                        border: "none",
                                                        padding: "8px 16px",
                                                        borderRadius: "4px"
                                                    }}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {/* Footer */}
            <Footer />
        </React.Fragment>

    );
};

export default EmiCalculate;